/**
 * The twin tree, assembled in the browser from the flat list GET /twins returns.
 *
 * Flat on the wire because the dashboard needs two shapes: a tree for the
 * navigation and a lookup by id for every device row that names its twin. A
 * nested payload makes the second a recursive walk; a flat one makes both cheap.
 */

export interface FlatTwin {
  id: string;
  parentId: string | null;
  title: string;
  deviceCount: number;
}

export interface TwinTreeNode<T extends FlatTwin = FlatTwin> {
  id: string;
  title: string;
  twin: T;
  /** Devices on this twin and everything below it. */
  deviceTotal: number;
  children: TwinTreeNode<T>[];
}

const byTitle = (a: { title: string }, b: { title: string }) =>
  a.title.localeCompare(b.title, 'de');

/**
 * Nest a flat list.
 *
 * A node is a root when its parent is null OR absent from the list. The server
 * already nulls a parent the caller cannot see (chapter 18); treating a missing
 * parent as a root anyway means a list that arrives filtered some other way
 * still renders every node rather than silently dropping a subtree.
 *
 * A cycle cannot come from the database's CHECK constraints alone (they forbid
 * a node being its own parent, not a longer loop), so the walk tracks what it
 * has placed and a node reachable only through a cycle becomes a root rather
 * than recursing forever.
 */
export function buildTree<T extends FlatTwin>(flat: readonly T[]): TwinTreeNode<T>[] {
  const ids = new Set(flat.map((t) => t.id));
  const children = new Map<string, T[]>();
  const roots: T[] = [];
  for (const twin of flat) {
    if (twin.parentId && ids.has(twin.parentId) && twin.parentId !== twin.id) {
      const list = children.get(twin.parentId) ?? [];
      list.push(twin);
      children.set(twin.parentId, list);
    } else {
      roots.push(twin);
    }
  }

  const placed = new Set<string>();
  function node(twin: T): TwinTreeNode<T> {
    placed.add(twin.id);
    const kids = (children.get(twin.id) ?? [])
      .filter((c) => !placed.has(c.id))
      .sort(byTitle)
      .map(node);
    return {
      id: twin.id,
      title: twin.title,
      twin,
      deviceTotal: twin.deviceCount + kids.reduce((sum, k) => sum + k.deviceTotal, 0),
      children: kids,
    };
  }

  const tree = roots.sort(byTitle).map(node);
  // Anything not placed sits on a cycle with no way in from a root. Shown as a
  // root, so it is visible and someone can fix the data.
  for (const twin of flat) if (!placed.has(twin.id)) tree.push(node(twin));
  return tree;
}

/** The ids from a root down to `id`, inclusive, or [] if it is not in the list. */
export function ancestry(flat: readonly FlatTwin[], id: string): string[] {
  const byId = new Map(flat.map((t) => [t.id, t]));
  const path: string[] = [];
  const seen = new Set<string>();
  let current = byId.get(id);
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    path.unshift(current.id);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return path;
}
